import * as React from 'react';

import { Autocomplete, Button, Chip, FormSelectField, FormTextField, Typography } from 'ui';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { Grid } from '@mui/material';
import {
  ListMetaTagFragment,
  SimpleMetaTagFragment,
  useMetaTagsByTaggableInternalQuery,
} from 'generated/graphql';
import { MetaTagSelector } from 'components';
import { StoreType, useActions } from 'utils/connect';
import { Theme, css } from '@emotion/react';
import { map as _map, sortBy } from 'es-toolkit/compat';
import { consumer } from '@tock/coretypes';
import { gql } from '@apollo/client';
import { tocktix } from '@tock/protos/dist/types/clowncar';
import { useSelector } from 'react-redux';
import protos from 'utils/protos';

const FETCH_MODE = 'ALL';

export interface BusinessCollectionDataConfigValues {
  name: string;
  pendingKeywordTerm: string;
  keywordTermMatchOne: string[];
  luceneQueryFilter: string;
  searchWindowStartDate: string;
  searchWindowNumDays?: number;
  offeringSearchType: consumer.OfferingSearchType;
  ticketTypeTags: tocktix.messages.IMetaTag[]; // type for wire operations is tocktix.messages.IMetaTag[];
  pendingTicketTypeTagName: string;
  businessTags: tocktix.messages.IMetaTag[];
  pendingBusinessTagName: string;
  metroArea: string; // type for wire operations is tocktix.messages.IMetroArea
  luceneQuerySpecific: string;
}

export const emptyState: BusinessCollectionDataConfigValues = {
  name: '',
  pendingKeywordTerm: '',
  keywordTermMatchOne: [],
  luceneQueryFilter: '',
  searchWindowStartDate: '',
  searchWindowNumDays: undefined,
  // We use 'EVERYTHING' here, instead of keeping this in the form as the empty string, because
  // using the empty string here as the form value will cause a defect whereby the "Reservation type"
  // label on the FormSelectField for ticket type will have a line drawn through it instead of the label
  // appearing over the line. Setting InputProps={{shrink: true}} on the FormSelectField doesn't help
  // with this.
  //
  // We just take care to serialize this accordingly on save and unserialize it accordingly on load.
  offeringSearchType: 'ALL_EXPERIENCES',
  ticketTypeTags: [],
  pendingTicketTypeTagName: '',
  businessTags: [],
  pendingBusinessTagName: '',
  metroArea: '',
  luceneQuerySpecific: '',
};

interface Props {
  initialValues: BusinessCollectionDataConfigValues;
  onSubmit(
    values: BusinessCollectionDataConfigValues,
    formikHelpers: FormikHelpers<BusinessCollectionDataConfigValues>
  ): Promise<void>;
  updatingConfig?: { id?: number | null; name?: string | null; isArchived?: boolean | null };
  clearUpdatingConfig(): void;
}

export const META_TAGS_BY_TAGGABLE_QUERY = gql`
  query MetaTagsByTaggableInternal {
    ticketTypeMetaTagsInternal {
      id
      ...simpleMetaTag
    }
    businessMetaTagsInternal {
      id
      ...simpleMetaTag
    }
  }
  fragment simpleMetaTag on MetaTagEntity {
    id
    name
    type
    isInternal
    isActive
  }
`;

const getTagMap = (tags: SimpleMetaTagFragment[]) => {
  const map = new Map<string, tocktix.messages.IMetaTag>();
  const tagsInternal = sortBy(tags, ['type', '!isActive']);

  tagsInternal.forEach((tag: ListMetaTagFragment) => {
    if (tag.name) {
      map.set(tag.name, tag);
    }
  });

  return map;
};

const { OfferingSearchType } = protos;

const OfferingSearchTypeDisplayNameMap: {
  [K in consumer.OfferingSearchType]: string;
} = {
  ALL_EXPERIENCES: 'All experiences',
  DINE_IN_EXPERIENCES: 'Dine-in',
  ONLINE_ORDERS: 'Pickup & delivery',
  ORDER_DELIVERY: 'Delivery',
  ORDER_PICKUP: 'Pickup',
  EVENT: 'Events',
};

const allOfferingSearchTypes = Object.keys(OfferingSearchType) as consumer.OfferingSearchType[];

const businessCollectionDataConfigFormContent = ({ spacing }: Theme) => css`
  height: 100%;
  padding-left: ${spacing(2)};
  padding-right: ${spacing(2)};
  padding-top: ${spacing(4)};
  & > * {
    padding-bottom: ${spacing(4)};
  }
`;

const BusinessCollectionDataConfigurationForm: React.FunctionComponent<
  React.PropsWithChildren<Props>
> = ({ initialValues, onSubmit, updatingConfig, clearUpdatingConfig }) => {
  const { actions } = useActions();
  const [metroAreaAutocomplete, setMetroAreaAutocomplete] = React.useState('');

  const { metroAreaList, requestInProgress: metrosInProgress } = useSelector(
    (state: StoreType) => state.metroArea
  );
  const { data: tagData } = useMetaTagsByTaggableInternalQuery();

  const ticketTypeTagMap = React.useMemo(
    () => getTagMap(tagData?.ticketTypeMetaTagsInternal || []),
    [tagData?.ticketTypeMetaTagsInternal]
  );

  const businessTagMap = React.useMemo(
    () => getTagMap(tagData?.businessMetaTagsInternal || []),
    [tagData?.businessMetaTagsInternal]
  );

  const sortedMetros = React.useMemo(() => {
    const values = _map(metroAreaList, 'name');
    values.sort();
    return values;
  }, [metroAreaList]);

  const handleSubmit = (
    values: BusinessCollectionDataConfigValues,
    formikHelpers: FormikHelpers<BusinessCollectionDataConfigValues>
  ) => {
    return onSubmit(values, formikHelpers).then(() => setMetroAreaAutocomplete(''));
  };

  return (
    <Formik<BusinessCollectionDataConfigValues>
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {({
        values,
        isSubmitting,
        setSubmitting,
        setFieldValue,
        setValues,
      }: FormikProps<BusinessCollectionDataConfigValues>) => {
        const saveButton = (
          <Button disabled={isSubmitting} color="success" size="large" type="submit">
            {updatingConfig?.id ? 'Update' : 'Create'} Config
          </Button>
        );

        const toggleCurrentConfigArchived = async () => {
          if (
            updatingConfig?.id &&
            confirm(updatingConfig.isArchived ? 'configUnarchive' : 'configArchive')
          ) {
            await actions.consumerPage.updateNamedConfiguration({
              ...updatingConfig,
              isArchived: !updatingConfig.isArchived,
            });
            await actions.consumerPage.fetchAllNamedConfigurations(FETCH_MODE);
          }
        };

        const resetFormAndValues = () => {
          clearUpdatingConfig();
          setMetroAreaAutocomplete('');
        };

        const removeCurrentConfig = async () => {
          if (updatingConfig?.id && confirm('configRemove')) {
            setSubmitting(true);
            await actions.consumerPage.deleteNamedConfiguration(updatingConfig.id);
            await actions.consumerPage.fetchAllNamedConfigurations(FETCH_MODE);
            resetFormAndValues();
            setSubmitting(false);
          }
        };

        return (
          <Form css={businessCollectionDataConfigFormContent}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="display1Bold" mb={2}>
                  {updatingConfig?.id
                    ? `Updating Configuration "${updatingConfig.name}" `
                    : 'New Configuration'}
                </Typography>
              </Grid>
              <Grid item xs={8}>
                <FormTextField
                  disabled={!!updatingConfig?.id}
                  fullWidth
                  label="Config Name (permanent, unique)"
                  name="name"
                  required
                />
              </Grid>
              <Grid item xs={4}>
                <Grid container justifyContent="flex-end">
                  {saveButton}
                </Grid>
              </Grid>
              <Grid item xs={4}>
                <FormTextField
                  name="searchWindowStartDate"
                  type="date"
                  label="Search window start"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={2}>
                <Button
                  disabled={values.searchWindowStartDate === ''}
                  onClick={() => setFieldValue('searchWindowStartDate', '')}
                >
                  Clear start date
                </Button>
              </Grid>
              <Grid item xs={6}>
                <FormTextField
                  name="searchWindowNumDays"
                  type="number"
                  label="Search window length in days [leave blank for 1 day]"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <FormSelectField
                  label="Reservation type"
                  name="offeringSearchType"
                  value={values.offeringSearchType}
                  fullWidth
                >
                  {allOfferingSearchTypes.map((offeringSearchType) => (
                    <option key={offeringSearchType} value={offeringSearchType}>
                      {OfferingSearchTypeDisplayNameMap[offeringSearchType]}
                    </option>
                  ))}
                </FormSelectField>
              </Grid>
              {/* just a spacer here: */}
              <Grid item xs={6} />
              <Grid item xs={6}>
                {/* Populate this appropriately, maybe make it typeahead */}
                <Autocomplete
                  options={sortedMetros}
                  value={metroAreaAutocomplete}
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  renderInput={(params: any) => (
                    <FormTextField
                      {...params}
                      label="Metro area [leave blank for any]"
                      name="metroArea"
                    />
                  )}
                  disabled={metrosInProgress}
                  onChange={(__, metroArea) => {
                    setFieldValue('metroArea', metroArea);
                    setMetroAreaAutocomplete(metroArea ?? '');
                  }}
                />
              </Grid>
              {/* just a spacer here: */}
              <Grid item xs={6} />
              <Grid item container xs={12}>
                <Grid item xs={3}>
                  {/* Note that in newer Material-UI, the rows-control properties are different */}
                  <FormTextField
                    name="pendingKeywordTerm"
                    label="Keyword Terms - Match one"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={2}>
                  <Button
                    disabled={values.pendingKeywordTerm.length === 0}
                    onClick={() => {
                      setFieldValue('keywordTermMatchOne', [
                        ...values.keywordTermMatchOne,
                        values.pendingKeywordTerm,
                      ]);
                      setFieldValue('pendingKeywordTerm', '');
                    }}
                  >
                    Add keyword
                  </Button>
                </Grid>
                <Grid item xs={2}>
                  <Button
                    disabled={values.keywordTermMatchOne.length === 0}
                    onClick={() => {
                      setFieldValue('keywordTermMatchOne', []);
                      setFieldValue('pendingKeywordTerm', '');
                    }}
                  >
                    Clear keywords
                  </Button>
                </Grid>
                <Grid item xs={5}>
                  Keywords:{' '}
                  {values.keywordTermMatchOne.map((keyword) => (
                    <Chip color="success" label={keyword} />
                  ))}
                </Grid>
                <div>
                  Items or experiences to be returned must match at least one of these keywords, if
                  any are specified. OR they will be returned if they match any of the campaign or
                  experience tags listed below
                </div>
              </Grid>
              <MetaTagSelector
                name="pendingTicketTypeTagName"
                label="Campaign or Experience Tag"
                defaultOptionLabel="[select experience tags here]"
                pendingTagName={values.pendingTicketTypeTagName}
                selectedTags={values.ticketTypeTags}
                addSelectedTag={(tag: tocktix.messages.IMetaTag) => {
                  setFieldValue('ticketTypeTags', [...values.ticketTypeTags, tag]);
                  setFieldValue('pendingTicketTypeTagName', '');
                }}
                clearSelectedTags={() => {
                  setFieldValue('ticketTypeTags', []);
                }}
                tagMap={ticketTypeTagMap}
                description="Items or experiences to be returned must match one of these campaign or experience tags, if any are specified.
                        OR they must match one of the business tags specified below.
                        OR they must match one of the keywords specified above (if any.) If you wish to match BOTH a campaign
                        tag AND an experience tag, use the lucene query field to handle that."
              />
              <MetaTagSelector
                name="pendingBusinessTagName"
                label="Buiness Campaign Tag"
                defaultOptionLabel="[select business tags here]"
                pendingTagName={values.pendingBusinessTagName}
                selectedTags={values.businessTags}
                addSelectedTag={(tag: tocktix.messages.IMetaTag) => {
                  setFieldValue('businessTags', [...values.businessTags, tag]);
                  setFieldValue('pendingBusinessTagName', '');
                }}
                clearSelectedTags={() => {
                  setFieldValue('businessTags', []);
                }}
                tagMap={businessTagMap}
                description="Businesses to be returned must match one of these campaign tags, if any are specified.
                        OR they must match one of the experience tags specified above.
                        OR they must match one of the keywords specified above (if any.) If you wish to match BOTH a campaign
                        tag AND an experience tag, use the lucene query field to handle that."
              />
              <Grid item container xs={12}>
                <Grid item xs={8}>
                  {/* Note that in newer Material-UI, the rows-control properties are different */}
                  <FormTextField
                    name="luceneQueryFilter"
                    label="Lucene Query String - Must Match"
                    fullWidth
                    multiline
                    rows={3}
                  />
                </Grid>
                <div>
                  Items or experiences to be returned must match this field to be included in the
                  collection results; it further restricts the results beyond any of the fields
                  above.
                </div>
              </Grid>

              <Grid item xs={6}>
                <Grid container>
                  {updatingConfig?.id && (
                    <Button size="large" color="primary" onClick={toggleCurrentConfigArchived}>
                      {updatingConfig.isArchived ? 'Unarchive' : 'Archive'} Config
                    </Button>
                  )}
                  &nbsp;
                  {updatingConfig?.id && (
                    <Button
                      size="large"
                      color="danger"
                      variant="text"
                      onClick={removeCurrentConfig}
                    >
                      Delete Config
                    </Button>
                  )}
                </Grid>
              </Grid>
              <Grid item xs={6}>
                <Grid container justifyContent="flex-end">
                  {saveButton}
                </Grid>
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};

export default BusinessCollectionDataConfigurationForm;
